import { EventData, UserEventProgress } from '@wwe-portal/entities/rbe.type';
import { S3_IMAGES_URL } from '@wwe-portal/web/utils/config';
import { FC, ReactNode } from 'react';
import Image from 'next/image';
import styles from './event-banner.module.css';
import { cn } from '@wwe-portal/ui/lib/lib';
import { ProgressBar } from '@wwe-portal/ui/components/progress';
import { EventMoreInfo } from './event-more-info';
import { getCurrentMilestone } from '@wwe-portal/web/utils/rbe/helper';
import { CountdownBadge } from './event-badge';
import { ClientOnly } from '../common/client-only';

export const EventBanner: FC<{
  eventData: EventData;
  userProgress: UserEventProgress | undefined;
  eventType: string;
  badge: ReactNode;
}> = ({ eventData, userProgress, eventType, badge }) => {
  const currentProgress = getCurrentMilestone(
    eventData.milestones,
    userProgress?.point
  );
  return (
    <div className="overflow-hidden rounded-xl">
      <div className="relative flex w-full items-start bg-cover bg-center bg-no-repeat md:items-stretch">
        <div className={cn('bg-midnightExpressShade3 absolute inset-0 -z-10')}>
          <div
            className={cn(
              '3xl:left-[40%] absolute inset-0 md:left-[40%] xl:left-[15%]',
              styles.bannerImage
            )}
          >
            <Image
              fill
              src={`${S3_IMAGES_URL}/${eventData.image}`}
              alt={eventData.name}
              className="bg-right object-cover object-right"
            />
          </div>
        </div>
        <div
          className={cn(
            'w-0 grow basis-0 pl-4 md:w-[55%] md:grow-0 md:basis-[55%] md:pl-8 md:pr-16 xl:w-[45%] xl:basis-[45%]',
            styles.bannerContent
          )}
        >
          <h3 className="text-blue700 text-xs font-medium uppercase">
            {eventType}
          </h3>
          <h4 className="text-text300 my-[10px] truncate text-sm font-semibold leading-[40px] md:text-2xl">
            {eventData.name}
          </h4>
          <div className="text-text300 text-md mt-3 hidden md:line-clamp-3">
            {eventData.description}
          </div>
        </div>
        <div className="pr-4 pt-4 md:w-[45%] md:basis-[45%] md:py-5 md:pr-[30px] xl:w-[55%] xl:basis-[55%]">
          <EventMoreInfo
            eventType={eventType}
            className="md:hidden"
            eventData={eventData}
            userProgress={userProgress}
            currentMilestone={currentProgress}
          />
          <div className="hidden h-full flex-col items-end justify-between md:flex">
            <ClientOnly>
              <CountdownBadge endDate={eventData.endDate} />
            </ClientOnly>
            {eventData.assetsBanner && eventData.assetsBanner.length > 0 && (
              <div className="flex w-full flex-row justify-center">
                {eventData.assetsBanner.map((asset) => {
                  return (
                    <div
                      key={asset.id}
                      className="relative mx-[5px] h-[90px] w-[60px] xl:h-[118px] xl:w-[94px]"
                    >
                      <Image
                        fill
                        src={asset.url}
                        alt="banner_asset"
                        className="absolute inset-0 object-contain"
                      />
                    </div>
                  );
                })}
              </div>
            )}

            <div className={styles.statisticBadgeWrapper}>{badge}</div>
          </div>
        </div>
      </div>
      {userProgress && (
        <div className="bg-back200 flex h-16 px-4 md:h-24 md:gap-5 md:px-8">
          <div className="flex items-center">
            <div className="relative h-16 w-16">
              <Image
                src="/assets/images/limited-time-events/event-trophy.webp"
                alt="Event Trophy"
                className="bg-center object-cover"
                width={64}
                height={64}
              />
              <div className={styles.bannerMilestone}>
                {currentProgress?.level ?? 1}
              </div>
            </div>
          </div>
          {currentProgress?.requirement != null && (
            <div className="flex flex-1 basis-0 items-center">
              <ProgressBar
                value={Math.min(
                  userProgress?.point ?? 0,
                  currentProgress.requirement
                )}
                maxValue={currentProgress.requirement}
                className="w-full"
                isShowMax
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
